import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Plus, Search, Loader2, Package, ClipboardList, Droplet, CircleDot } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '@/components/ui/Sheet';
import { apiClient, extractErrorMessage } from '@/lib/api';
// PR-NOBYPASS — this used to open EventDrawer, whose only action was "Mark
// paid" from whatever stage the job was standing at. That button is the reason
// ninety-six of a hundred and two jobs never had a diagnosis. Same drawer
// position, same job, but the ladder instead of the shortcut.
import { JobStepper } from '@/components/maintenance/JobStepper';
import { OilDrumsPanel, TiresPanel, TIRE_BRANDS } from '@/pages/maintenance/ConsumablesPanels';

/**
 * Spare parts, in the two halves the job actually has.
 *
 * WAREHOUSE — what is on the shelf, and a way to put more on it. Until stock
 * can be received in the UI there is nothing for a technician to consume, which
 * is the whole reason the usage table sat empty.
 *
 * LEDGER — what has been consumed, read from maintenance_parts_used. It is not
 * derived from the warehouse view and does not derive it: one says what we have,
 * the other says what we spent, and a single table pretending to do both ends up
 * lying about one of them.
 */

interface CatalogRow {
  id: string; code: string | null; part_number: string | null; name_en: string;
  category: string | null; last_purchase_price_sar: string | null; total_stock: string | null;
}
interface UsedRow {
  id: string; created_at: string; quantity: string; unit_cost_sar: string; total_cost_sar: string;
  source: string; part_name: string | null; part_number: string | null;
  event_id: string; event_code: string; event_status: string;
  vehicle_plate: string | null; vehicle_code: string | null; logged_by: string | null;
}

const sar = (v: unknown) =>
  (v == null ? '—' : Number(v).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }));
const day = (v: string) => String(v).slice(0, 10);

/**
 * The lists behind every dropdown on this form.
 *
 * Typed-in values drift - "Michelin", "michelin", "MICHLIN" become three brands
 * that no report can add together. Every list ends in Custom, because a fleet
 * always meets something the list did not predict, and refusing it would send
 * people back to typing.
 */
const CUSTOM = 'Custom';
const OIL_GRADES = ['15W40', '20W50', '10W40'];
const OIL_BRANDS = ['Shell', 'Mobil'];
const TYRE_BRANDS = ['Michelin', 'Kinto', 'Bridgestone', 'Steer', 'Drive', 'Santos', 'Magna'];
const TYRE_SIZES = ['315/80R22.5', '385/80R22.5'];
const FILTER_TYPES = ['Oil filter', 'Air filter', 'Diesel filter', 'AC filter'];
const COMPONENT_BRANDS = ['FAW', 'Sany'];
const GREASE_BRANDS = ['Mobil', 'Sany', 'FAW', 'Shell'];

/** A select whose last option reveals a free-text box. */
function PickOrType({ label, options, value, onChange, placeholder }: {
  label: string; options: string[]; value: string;
  onChange: (v: string) => void; placeholder?: string;
}) {
  const known = options.includes(value);
  const [custom, setCustom] = useState(!known && value !== '');
  return (
    <div className="space-y-1">
      <label className="text-xs text-muted-foreground">{label}</label>
      <select value={custom ? CUSTOM : value}
        onChange={(e) => {
          if (e.target.value === CUSTOM) { setCustom(true); onChange(''); }
          else { setCustom(false); onChange(e.target.value); }
        }}
        className="h-10 w-full rounded-md border border-input bg-background px-2 text-sm">
        <option value="">— select —</option>
        {options.map((o) => <option key={o} value={o}>{o}</option>)}
        <option value={CUSTOM}>{CUSTOM}</option>
      </select>
      {custom && (
        <Input value={value} onChange={(e) => onChange(e.target.value)}
          placeholder={placeholder ?? 'Type it in'} />
      )}
    </div>
  );
}

export function SparePartsPanel({ typeLabel }: { typeLabel: (t: string) => string }) {
  const qc = useQueryClient();
  const [sub, setSub] = useState<'stock' | 'used' | 'oil' | 'tires'>('stock');
  const [q, setQ] = useState('');
  const [addOpen, setAddOpen] = useState(false);
  const [openEvent, setOpenEvent] = useState<string | null>(null);
  const [confirmRemove, setConfirmRemove] = useState<CatalogRow | null>(null);
  const [removeNote, setRemoveNote] = useState<string | null>(null);

  const removePart = useMutation({
    mutationFn: async (id: string) =>
      (await apiClient.delete<{ retired?: boolean; message?: string }>(`/api/v1/maintenance/parts/${id}`)).data,
    onSuccess: (r) => {
      setConfirmRemove(null);
      setRemoveNote(r.retired ? (r.message ?? 'Retired.') : null);
      void qc.invalidateQueries({ queryKey: ['parts-catalog'] });
    },
  });

  const { data: catalog, isLoading: loadingStock } = useQuery({
    queryKey: ['parts-catalog', q],
    queryFn: async () => (await apiClient.get<CatalogRow[]>('/api/v1/maintenance/parts',
      { params: q.trim() ? { search: q.trim() } : {} })).data,
  });

  const { data: used, isLoading: loadingUsed } = useQuery({
    queryKey: ['parts-used', q],
    enabled: sub === 'used',
    queryFn: async () => (await apiClient.get<{ items: UsedRow[] }>('/api/v1/maintenance/parts-used',
      { params: q.trim() ? { search: q.trim() } : {} })).data.items,
  });

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center gap-2">
        <div className="inline-flex rounded-lg border border-border overflow-hidden">
          {([
            ['stock', 'Inventory & stock', Package],
            ['used', 'Parts used', ClipboardList],
            ['oil', 'Oil & fluids', Droplet],
            ['tires', 'Tyres', CircleDot],
          ] as const).map(([v, l, I]) => (
            <button key={v} onClick={() => setSub(v)}
              className={'px-3 py-1.5 text-sm inline-flex items-center gap-1.5 transition ' + (sub === v
                ? 'bg-muted font-medium' : 'text-muted-foreground hover:text-foreground')}>
              <I className="h-3.5 w-3.5" /> {l}
            </button>
          ))}
        </div>

        {(sub === 'stock' || sub === 'used') && (
        <div className="relative flex-1 min-w-[12rem] max-w-sm">
          <Search className="h-4 w-4 absolute start-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} className="ps-9"
            placeholder={sub === 'stock' ? 'Part name or number…' : 'Part, plate or job code…'} />
        </div>
        )}

        {sub === 'stock' && (
          <Button size="sm" className="gap-2 ms-auto" onClick={() => setAddOpen(true)}>
            <Plus className="h-4 w-4" /> Add spare part
          </Button>
        )}
      </div>

      {/* ------------------------------------------------------ WAREHOUSE */}
      {sub === 'stock' && (
        loadingStock ? (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
          </div>
        ) : (catalog ?? []).length === 0 ? (
          <div className="rounded-lg border border-dashed border-border p-10 text-center">
            <Package className="h-8 w-8 mx-auto text-muted-foreground" />
            <div className="mt-3 font-medium">
              {q.trim() ? `Nothing matches “${q.trim()}”.` : 'The catalogue is empty'}
            </div>
            <p className="text-sm text-muted-foreground mt-1 max-w-md mx-auto">
              Nothing can be consumed on a job until something is on the shelf. Add the parts you
              actually stock — filters, pads, tyres — and they become pickable during an inspection.
            </p>
            {!q.trim() && (
              <Button size="sm" className="mt-4 gap-2" onClick={() => setAddOpen(true)}>
                <Plus className="h-4 w-4" /> Add the first part
              </Button>
            )}
          </div>
        ) : (
          <div className="rounded-xl border border-border overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-muted/40 text-muted-foreground text-xs sticky top-0">
                <tr>
                  <th className="text-start px-3 py-2 font-medium">SKU</th>
                  <th className="text-start px-3 py-2 font-medium">Name</th>
                  <th className="text-end px-3 py-2 font-medium">In stock</th>
                  <th className="text-end px-3 py-2 font-medium">Reorder at</th>
                  <th className="text-end px-3 py-2 font-medium">Unit cost</th>
                  <th className="text-end px-3 py-2 font-medium">Total value</th>
                  <th className="px-3 py-2" />
                </tr>
              </thead>
              <tbody>
                {(catalog ?? []).map((c) => {
                  const stock = Number(c.total_stock ?? 0);
                  const cost = Number(c.last_purchase_price_sar ?? 0);
                  // Reorder level lives per location; until one is set the only
                  // honest threshold is "none left".
                  const reorder = 0;
                  const low = stock <= reorder;
                  return (
                    <tr key={c.id} className={'border-t border-border ' + (low ? 'bg-amber-500/10' : '')}>
                      <td className="px-3 py-2 font-mono text-xs">{c.part_number ?? c.code ?? '—'}</td>
                      <td className="px-3 py-2">
                        {c.name_en}
                        {c.category && <span className="text-xs text-muted-foreground"> · {c.category}</span>}
                      </td>
                      <td className={'px-3 py-2 text-end font-mono ' + (low ? 'text-amber-700 dark:text-amber-400 font-medium' : '')}>
                        {stock}
                      </td>
                      <td className="px-3 py-2 text-end font-mono text-muted-foreground">{reorder}</td>
                      <td className="px-3 py-2 text-end font-mono">{sar(cost)}</td>
                      <td className="px-3 py-2 text-end font-mono">{sar(stock * cost)}</td>
                      <td className="px-3 py-2 text-end">
                        <button type="button" onClick={() => setConfirmRemove(c)}
                          className="text-xs text-muted-foreground hover:text-rose-600 dark:hover:text-rose-400 transition-colors">
                          Delete
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )
      )}

      {/* --------------------------------------------------------- LEDGER */}
      {sub === 'used' && (
        loadingUsed ? (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
          </div>
        ) : (used ?? []).length === 0 ? (
          <div className="rounded-lg border border-dashed border-border p-10 text-center">
            <ClipboardList className="h-8 w-8 mx-auto text-muted-foreground" />
            <div className="mt-3 font-medium">No parts logged against a job yet</div>
            <p className="text-sm text-muted-foreground mt-1 max-w-md mx-auto">
              Parts appear here as they are fitted during an extraordinary inspection. Each line
              carries its job, so a cost can always be traced back to the truck that caused it.
            </p>
          </div>
        ) : (
          <div className="rounded-xl border border-border overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-muted/40 text-muted-foreground text-xs sticky top-0">
                <tr>
                  <th className="text-start px-3 py-2 font-medium">Date</th>
                  <th className="text-start px-3 py-2 font-medium">Part</th>
                  <th className="text-end px-3 py-2 font-medium">Qty</th>
                  <th className="text-start px-3 py-2 font-medium">Truck</th>
                  <th className="text-start px-3 py-2 font-medium">Job</th>
                  <th className="text-end px-3 py-2 font-medium">Cost SAR</th>
                  <th className="text-start px-3 py-2 font-medium">Logged by</th>
                </tr>
              </thead>
              <tbody>
                {(used ?? []).map((u) => (
                  <tr key={u.id} className="border-t border-border hover:bg-muted/20">
                    <td className="px-3 py-2 font-mono text-xs whitespace-nowrap">{day(u.created_at)}</td>
                    <td className="px-3 py-2">
                      {u.part_name ?? '—'}
                      {u.source === 'fresh_purchase' && (
                        <span className="text-[11px] text-muted-foreground ms-1.5">one-off</span>
                      )}
                    </td>
                    <td className="px-3 py-2 text-end font-mono">{Number(u.quantity)}</td>
                    <td className="px-3 py-2">
                      <button onClick={() => setOpenEvent(u.event_id)}
                        className="font-mono underline underline-offset-2 hover:text-foreground text-muted-foreground">
                        {u.vehicle_plate ?? u.vehicle_code ?? '—'}
                      </button>
                    </td>
                    <td className="px-3 py-2">
                      <button onClick={() => setOpenEvent(u.event_id)}
                        className="font-mono text-xs underline underline-offset-2 hover:text-foreground text-muted-foreground">
                        {u.event_code}
                      </button>
                    </td>
                    <td className="px-3 py-2 text-end font-mono">{sar(u.total_cost_sar)}</td>
                    <td className="px-3 py-2 text-muted-foreground">{u.logged_by ?? '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )
      )}

      {sub === 'oil' && <OilDrumsPanel />}
      {sub === 'tires' && <TiresPanel />}

      {removeNote && <p className="text-xs text-muted-foreground">{removeNote}</p>}

      {/* Asked for once, by name: a delete link in a dense table is easy to
          press by accident. */}
      {confirmRemove && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
             role="dialog" aria-modal="true" onClick={() => setConfirmRemove(null)}>
          <div className="bg-card border border-border rounded-xl p-4 w-full max-w-sm space-y-3"
               onClick={(e) => e.stopPropagation()}>
            <div className="font-medium">Delete "{confirmRemove.name_en}"?</div>
            <p className="text-sm text-muted-foreground">
              If it has been fitted to a job it is retired instead, so those jobs keep their history.
            </p>
            <div className="flex gap-2">
              <Button variant="outline" className="flex-1" onClick={() => setConfirmRemove(null)}>Cancel</Button>
              <Button variant="gold" className="flex-1" disabled={removePart.isPending}
                onClick={() => removePart.mutate(confirmRemove.id)}>
                {removePart.isPending ? 'Working...' : 'Delete'}
              </Button>
            </div>
          </div>
        </div>
      )}

      {addOpen && <AddPartSheet onClose={() => setAddOpen(false)} onSaved={() => {
        setAddOpen(false);
        void qc.invalidateQueries({ queryKey: ['parts-catalog'] });
        void qc.invalidateQueries({ queryKey: ['oil-drums'] });
        void qc.invalidateQueries({ queryKey: ['tires'] });
      }} />}

      {openEvent && (
        <JobStepper eventId={openEvent} typeLabel={typeLabel} onClose={() => setOpenEvent(null)} />
      )}
    </div>
  );
}

/**
 * Receiving stock: create the catalogue entry, then put the quantity on a shelf.
 *
 * Two calls because they are two facts — what a part IS, and how many we have of
 * it in one place. Conflating them makes a second delivery of the same part
 * create a second part.
 */
/**
 * One labelled field.
 *
 * Module scope on purpose. Declared inside the sheet it became a new component
 * type on every render, so React unmounted the input after each keystroke and
 * focus was lost — you had to click again for every character.
 */
function Field({ label, children, hint }: {
  label: string; children: React.ReactNode; hint?: string;
}) {
  return (
    <div className="space-y-1">
      <label className="text-xs text-muted-foreground">{label}</label>
      {children}
      {hint && <p className="text-[11px] text-muted-foreground">{hint}</p>}
    </div>
  );
}

function AddPartSheet({ onClose, onSaved }: { onClose: () => void; onSaved: () => void }) {
  /**
   * One button, three things.
   *
   * A tyre and a drum of oil are not spare parts - one is serialised, the other
   * is a volume - but the person adding them is doing the same job and reaching
   * for the same button. The kind is asked first and the form becomes the right
   * form, rather than hiding two of them on other screens.
   */
  type Kind = 'part' | 'tire' | 'oil' | 'air_suspension' | 'shock_absorber' | 'filter' | 'grease';
  const [kind, setKind] = useState<Kind>('part');
  const [brand, setBrand] = useState('');
  const [subtype, setSubtype] = useState('');
  const [name, setName] = useState('');
  const [sku, setSku] = useState('');
  const [category, setCategory] = useState('');
  const [qty, setQty] = useState('');
  const [cost, setCost] = useState('');
  const [location, setLocation] = useState('main_store');
  const [err, setErr] = useState<string | null>(null);

  const save = useMutation({
    mutationFn: async () => {
      if (!name.trim()) throw new Error('A part needs a name.');
      if (kind === 'tire') {
        if (!sku.trim()) throw new Error('A tyre is identified by its serial number.');
        return apiClient.post('/api/v1/consumables/tires', {
          serialNumber: sku.trim(),
          brand: category.trim() || TIRE_BRANDS[0],
          ...(name.trim() && { sizeModel: name.trim() }),
          ...(Number(cost) > 0 && { unitCostSar: Number(cost) }),
        });
      }
      if (kind === 'oil') {
        if (!(Number(qty) > 0)) throw new Error('How many litres does the drum hold?');
        return apiClient.post('/api/v1/consumables/oil-drums', {
          label: name.trim() || 'Drum',
          grade: category.trim() || '15W40',
          ...(brand.trim() && { brand: brand.trim() }),
          capacityLiters: Number(qty),
          purchasePriceSar: Number(cost) || 0,
        });
      }
      if (kind !== 'part') {
        // Suspension, shocks and filters are countable parts. They get their own
        // entry in the form because their brands and types differ, but they live
        // in the catalogue like everything else that is fitted whole.
        const KIND_LABEL: Record<string, string> = {
          air_suspension: 'Air suspension', shock_absorber: 'Shock absorber',
          filter: 'Filter', grease: 'Greece',
        };
        const what = kind === 'filter' ? (subtype.trim() || 'Filter') : KIND_LABEL[kind];
        if (kind === 'filter' && !subtype.trim()) throw new Error('Which filter is it?');
        const partName = [brand.trim(), what].filter(Boolean).join(' ');
        const created = await apiClient.post<{ id: string }>('/api/v1/maintenance/parts', {
          nameEn: partName,
          ...(sku.trim() && { partNumber: sku.trim() }),
          category: kind,
          // Counted in kilograms, not in pieces.
          ...(kind === 'grease' && { unit: 'kg' }),
        });
        if (Number(qty) > 0) {
          await apiClient.post(`/api/v1/maintenance/inventory/${created.data.id}/adjust`, {
            location: location.trim() || 'main_store',
            quantity: Number(qty),
            ...(Number(cost) > 0 && { unitCostSar: Number(cost) }),
            reason: 'stock_received',
          });
        }
        return created;
      }
      const part = await apiClient.post<{ id: string }>('/api/v1/maintenance/parts', {
        nameEn: name.trim(),
        ...(sku.trim() && { partNumber: sku.trim() }),
        ...(category.trim() && { category: category.trim() }),
      });
      if (Number(qty) > 0) {
        await apiClient.post(`/api/v1/maintenance/inventory/${part.data.id}/adjust`, {
          location: location.trim() || 'main_store',
          quantity: Number(qty),
          ...(Number(cost) > 0 && { unitCostSar: Number(cost) }),
          reason: 'stock_received',
        });
      }
    },
    onSuccess: onSaved,
    onError: (e) => setErr(extractErrorMessage(e)),
  });

  return (
    <Sheet open onOpenChange={(o) => { if (!o) onClose(); }}>
      <SheetContent side="right" className="sm:max-w-md overflow-y-auto">
        <SheetHeader><SheetTitle>Add to stock</SheetTitle></SheetHeader>
        <div className="space-y-4 mt-4">
          <Field label="What are you adding?">
            <select value={kind} onChange={(e) => { setKind(e.target.value as Kind); setBrand(''); setSubtype(''); }}
              className="h-10 w-full rounded-md border border-input bg-background px-2 text-sm">
              <option value="part">Spare part</option>
              <option value="tire">Tyre</option>
              <option value="oil">Oil &amp; fluids</option>
              <option value="air_suspension">Air suspension</option>
              <option value="shock_absorber">Shock absorber</option>
              <option value="filter">Filter</option>
              <option value="grease">Greece</option>
            </select>
          </Field>

          {kind === 'tire' ? (
            <>
              <Field label="Serial number *">
                <Input value={sku} onChange={(e) => setSku(e.target.value)} placeholder="As moulded on the tyre" />
              </Field>
              <PickOrType label="Brand" options={TYRE_BRANDS} value={category}
                onChange={setCategory} placeholder="Brand name" />
              <div className="grid grid-cols-2 gap-3">
                <PickOrType label="Size / model" options={TYRE_SIZES} value={name}
                  onChange={setName} placeholder="e.g. 295/80R22.5" />
                <Field label="Unit cost SAR">
                  <Input inputMode="decimal" value={cost} onChange={(e) => setCost(e.target.value)} placeholder="0" />
                </Field>
              </div>
            </>
          ) : kind === 'oil' ? (
            <>
              <Field label="Drum label *">
                <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="Drum #01" />
              </Field>
              <div className="grid grid-cols-2 gap-3">
                <PickOrType label="Grade" options={OIL_GRADES} value={category}
                  onChange={setCategory} placeholder="e.g. 5W30" />
                <PickOrType label="Brand" options={OIL_BRANDS} value={brand}
                  onChange={setBrand} placeholder="Brand name" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <Field label="Capacity (litres) *">
                  <Input inputMode="decimal" value={qty} onChange={(e) => setQty(e.target.value)} placeholder="208" />
                </Field>
                <Field label="Purchase price SAR">
                  <Input inputMode="decimal" value={cost} onChange={(e) => setCost(e.target.value)} placeholder="0" />
                </Field>
              </div>
              {Number(qty) > 0 && Number(cost) > 0 && (
                <div className="rounded-lg border border-border px-3 py-2 text-sm">
                  Cost per litre: <b className="font-mono">{(Number(cost) / Number(qty)).toFixed(4)} SAR</b>
                </div>
              )}
            </>
          ) : kind !== 'part' ? (
            <>
              {kind === 'filter' && (
                <PickOrType label="Filter type *" options={FILTER_TYPES} value={subtype}
                  onChange={setSubtype} placeholder="e.g. hydraulic filter" />
              )}
              <PickOrType label="Brand" options={kind === 'grease' ? GREASE_BRANDS : COMPONENT_BRANDS}
                value={brand} onChange={setBrand} placeholder="Brand name" />
              <div className="grid grid-cols-2 gap-3">
                {kind === 'grease' ? (
                  <Field label="Weight (kg)">
                    <Input inputMode="decimal" value={qty} onChange={(e) => setQty(e.target.value)} placeholder="0" />
                  </Field>
                ) : (
                  <Field label="Part number">
                    <Input value={sku} onChange={(e) => setSku(e.target.value)} placeholder="optional" />
                  </Field>
                )}
                <Field label={kind === 'grease' ? 'Cost SAR per kg' : 'Unit cost SAR'}>
                  <Input inputMode="decimal" value={cost} onChange={(e) => setCost(e.target.value)} placeholder="0" />
                </Field>
              </div>
              <div className="grid grid-cols-2 gap-3">
                {kind !== 'grease' && (
                  <Field label="Quantity received">
                    <Input inputMode="numeric" value={qty} onChange={(e) => setQty(e.target.value)} placeholder="0" />
                  </Field>
                )}
                <Field label="Shelf location">
                  <Input value={location} onChange={(e) => setLocation(e.target.value)} placeholder="main_store" />
                </Field>
              </div>
              {kind === 'grease' && Number(qty) > 0 && Number(cost) > 0 && (
                <div className="rounded-lg border border-border px-3 py-2 text-sm">
                  Total: <b className="font-mono">{(Number(qty) * Number(cost)).toFixed(2)} SAR</b>
                  <span className="text-muted-foreground"> for {Number(qty)} kg</span>
                </div>
              )}
            </>
          ) : (
          <>
          <Field label="Part name *">
            <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. Front brake pad set" />
          </Field>
          <div className="grid grid-cols-2 gap-3">
            <Field label="SKU / part number">
              <Input value={sku} onChange={(e) => setSku(e.target.value)} placeholder="e.g. BP-1042" />
            </Field>
            <Field label="Category">
              <Input value={category} onChange={(e) => setCategory(e.target.value)} placeholder="e.g. brakes" />
            </Field>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Quantity received">
              <Input inputMode="numeric" value={qty} onChange={(e) => setQty(e.target.value)} placeholder="0" />
            </Field>
            <Field label="Unit cost SAR">
              <Input inputMode="decimal" value={cost} onChange={(e) => setCost(e.target.value)} placeholder="0" />
            </Field>
          </div>
          <Field label="Shelf location" hint="Where it physically sits. Stock is counted per location.">
            <Input value={location} onChange={(e) => setLocation(e.target.value)} placeholder="main_store" />
          </Field>
          </>
          )}

          {err && (
            <p className="text-sm text-rose-600 dark:text-rose-400">{err}</p>
          )}

          <div className="flex gap-2 pt-2">
            <Button variant="outline" className="flex-1" onClick={onClose}>Cancel</Button>
            <Button variant="gold" className="flex-1" disabled={save.isPending}
              onClick={() => { setErr(null); save.mutate(); }}>
              {save.isPending ? 'Saving...'
                : kind === 'tire' ? 'Add tyre'
                : kind === 'oil' ? 'Add drum'
                : kind === 'grease' ? 'Add Greece' : 'Add to stock'}
            </Button>
          </div>
        </div>
      </SheetContent>
    </Sheet>
  );
}
